import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { TESTIMONIALS } from "../../data/content";

export const Testimonials = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 40 });
    const [selected, setSelected] = useState(0);
    const [paused, setPaused] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        onSelect();
        return () => emblaApi.off("select", onSelect);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi || paused) return;
        timer.current = setInterval(() => emblaApi.scrollNext(), 5200);
        return () => clearInterval(timer.current);
    }, [emblaApi, paused]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative overflow-hidden bg-emerald py-28 lg:py-36"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <Quote
                className="pointer-events-none absolute -top-10 left-1/2 h-64 w-64 -translate-x-1/2 text-gold/10"
                strokeWidth={0.6}
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-4xl px-6 lg:px-10">
                <SectionHeading dark eyebrow="Kind Words" title="Letters From Our Clients" />

                <div className="mt-14 overflow-hidden" ref={emblaRef} data-testid="testimonial-carousel">
                    <div className="flex">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="min-w-0 flex-[0_0_100%] px-4 text-center" data-testid={`testimonial-slide-${i}`}>
                                <Quote className="mx-auto h-9 w-9 text-gold" strokeWidth={1.2} aria-hidden="true" />
                                <blockquote className="mx-auto mt-6 max-w-2xl font-script text-3xl leading-snug text-ivory sm:text-4xl">
                                    {t.quote}
                                </blockquote>
                                <div className="mt-8 flex items-center justify-center gap-4" aria-hidden="true">
                                    <span className="h-px w-10 bg-gold/50" />
                                    <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                                    <span className="h-px w-10 bg-gold/50" />
                                </div>
                                <p className="mt-5 font-serif text-lg tracking-[0.2em] text-gold">{t.name}</p>
                                <p className="mt-1 text-[11px] uppercase tracking-[0.3em] text-ivory/60">{t.event}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-12 flex items-center justify-center gap-8">
                    <button
                        data-testid="testimonial-prev-btn"
                        onClick={scrollPrev}
                        className="rounded-full border border-gold/40 p-2.5 text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald"
                        aria-label="Previous testimonial"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="flex gap-3" data-testid="testimonial-dots">
                        {TESTIMONIALS.map((_, i) => (
                            <button
                                key={i}
                                data-testid={`testimonial-dot-${i}`}
                                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                                aria-label={`Go to testimonial ${i + 1}`}
                                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ease-elegant ${
                                    selected === i ? "w-8 bg-gold" : "w-1.5 bg-gold/35 hover:bg-gold/60"
                                }`}
                            />
                        ))}
                    </div>
                    <button
                        data-testid="testimonial-next-btn"
                        onClick={scrollNext}
                        className="rounded-full border border-gold/40 p-2.5 text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald"
                        aria-label="Next testimonial"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>
        </section>
    );
};
